import { useEffect, useState } from "react";
import { adminApi, api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, X, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { DEFAULT_CONTENT, useContent } from "../lib/content";
import ImageInput from "../components/ImageInput";

const STATUS_OPTS = ["pending", "confirmed", "ready", "picked_up", "cancelled"];

const empty = {
  name: "",
  description: "",
  price: "",
  category: "statues",
  image: "",
  images_text: "",
  badge: "",
  in_stock: true,
};

// Content sections for the admin form
const CONTENT_SECTIONS = [
  {
    title: "Brand & header",
    fields: [
      ["brand_name", "text", "Brand name"],
      ["brand_tagline", "text", "Brand tagline"],
    ],
  },
  {
    title: "Home — Hero",
    fields: [
      ["hero_image", "url", "Hero image (Holy Family / devotional)"],
      ["hero_eyebrow", "text", "Eyebrow line"],
      ["hero_title_line1", "text", "Title line 1"],
      ["hero_title_line2", "text", "Title line 2 (highlighted)"],
      ["hero_subtitle", "textarea", "Subtitle paragraph"],
      ["hero_cta_primary", "text", "Primary button label"],
      ["hero_cta_secondary", "text", "Secondary button label"],
    ],
  },
  {
    title: "Home — Categories section",
    fields: [
      ["categories_eyebrow", "text", "Eyebrow"],
      ["categories_title", "text", "Title"],
    ],
  },
  {
    title: "Home — Featured products",
    fields: [
      ["featured_title", "text", "Section title"],
      ["featured_subtitle", "textarea", "Subtitle"],
    ],
  },
  {
    title: "Home — Rituals section",
    fields: [
      ["rituals_eyebrow", "text", "Eyebrow"],
      ["rituals_title_line1", "text", "Title line 1"],
      ["rituals_title_line2", "text", "Title line 2 (highlighted)"],
      ["rituals_subtitle", "textarea", "Subtitle"],
      ["ritual_card_1_image", "url", "Card 1 image"],
      ["ritual_card_1_eyebrow", "text", "Card 1 eyebrow"],
      ["ritual_card_1_title", "text", "Card 1 title"],
      ["ritual_card_1_text", "textarea", "Card 1 text"],
      ["ritual_card_2_image", "url", "Card 2 image"],
      ["ritual_card_2_eyebrow", "text", "Card 2 eyebrow"],
      ["ritual_card_2_title", "text", "Card 2 title"],
      ["ritual_card_2_text", "textarea", "Card 2 text"],
      ["ritual_card_3_image", "url", "Card 3 image"],
      ["ritual_card_3_eyebrow", "text", "Card 3 eyebrow"],
      ["ritual_card_3_title", "text", "Card 3 title"],
      ["ritual_card_3_text", "textarea", "Card 3 text"],
    ],
  },
  {
    title: "Home — Visit section",
    fields: [
      ["visit_image", "url", "Visit section image"],
      ["visit_section_eyebrow", "text", "Eyebrow"],
      ["visit_section_title", "text", "Title"],
      ["visit_section_paragraph", "textarea", "Paragraph"],
      ["visit_section_cta", "text", "Button label"],
    ],
  },
  {
    title: "Home — Trust strip",
    fields: [
      ["trust_1_title", "text", "Item 1 title"],
      ["trust_1_subtitle", "text", "Item 1 subtitle"],
      ["trust_2_title", "text", "Item 2 title"],
      ["trust_2_subtitle", "text", "Item 2 subtitle"],
      ["trust_3_title", "text", "Item 3 title"],
      ["trust_3_subtitle", "text", "Item 3 subtitle"],
      ["trust_4_title", "text", "Item 4 title"],
      ["trust_4_subtitle", "text", "Item 4 subtitle"],
    ],
  },
  {
    title: "Visit page",
    fields: [
      ["visit_page_eyebrow", "text", "Eyebrow"],
      ["visit_page_title_part1", "text", "Title part 1"],
      ["visit_page_title_part2", "text", "Title part 2 (highlighted)"],
      ["visit_page_subtitle", "textarea", "Subtitle paragraph"],
      ["visit_page_cta", "text", "Maps button label"],
    ],
  },
  {
    title: "Visit page — Gallery",
    fields: [
      ["gallery_eyebrow", "text", "Eyebrow"],
      ["gallery_title", "text", "Title"],
      ["gallery_subtitle", "textarea", "Subtitle"],
      ["gallery_image_1", "url", "Photo 1"],
      ["gallery_image_2", "url", "Photo 2"],
      ["gallery_image_3", "url", "Photo 3"],
      ["gallery_image_4", "url", "Photo 4"],
    ],
  },
  {
    title: "Visit page — Map",
    fields: [
      ["map_eyebrow", "text", "Eyebrow"],
      ["map_title", "text", "Title"],
      ["map_paragraph", "textarea", "Paragraph"],
      ["map_cta", "text", "Button label"],
      ["map_lat", "text", "Map latitude"],
      ["map_lng", "text", "Map longitude"],
    ],
  },
  {
    title: "Store details",
    fields: [
      ["store_address", "textarea", "Address"],
      ["store_phone", "text", "Phone"],
      ["store_hours", "text", "Hours"],
      ["store_email", "text", "Email"],
    ],
  },
  {
    title: "Footer",
    fields: [
      ["footer_tagline", "textarea", "Footer tagline paragraph"],
    ],
  },
  {
    title: "Home — New Arrivals",
    fields: [
      ["new_arrivals_eyebrow", "text", "Eyebrow"],
      ["new_arrivals_title", "text", "Title"],
      ["new_arrivals_subtitle", "textarea", "Subtitle"],
    ],
  },
];

export default function Admin() {
  const [password, setPassword] = useState(localStorage.getItem("srishti_admin_pwd") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [openSection, setOpenSection] = useState(0);
  const { refresh: refreshContent } = useContent();

  useEffect(() => {
    if (password) tryLogin(password);
    api.get("/categories").then((r) => setCategories(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryLogin = async (pwd) => {
    try {
      await api.post("/admin/login", { password: pwd });
      localStorage.setItem("srishti_admin_pwd", pwd);
      setAuthed(true);
      loadAll(pwd);
    } catch {
      toast.error("Invalid password");
      setAuthed(false);
      localStorage.removeItem("srishti_admin_pwd");
    }
  };

  const loadAll = async (pwd) => {
    const client = adminApi(pwd);
    const [pRes, oRes, sRes] = await Promise.all([
      api.get("/products"),
      client.get("/admin/orders"),
      api.get("/settings"),
    ]);
    setProducts(pRes.data);
    setOrders(oRes.data);
    setContent({ ...DEFAULT_CONTENT, ...sRes.data });
  };

  const saveContent = async (e) => {
    e.preventDefault();
    try {
      await adminApi(password).put("/admin/settings", content);
      refreshContent();
      toast.success("Content saved — site is updated");
    } catch {
      toast.error("Failed to save content");
    }
  };

  const submitProduct = async (e) => {
    e.preventDefault();
    const client = adminApi(password);
    const images = (form.images_text || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      category: form.category,
      image: form.image,
      images,
      badge: form.badge || null,
      in_stock: form.in_stock,
    };
    try {
      if (editing) {
        await client.put(`/admin/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await client.post("/admin/products", payload);
        toast.success("Product added");
      }
      setForm(empty);
      setEditing(null);
      loadAll(password);
    } catch {
      toast.error("Failed to save product");
    }
  };

  const editProduct = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name,
      description: p.description,
      price: String(p.price),
      category: p.category,
      image: p.image,
      images_text: (p.images || []).join("\n"),
      badge: p.badge || "",
      in_stock: p.in_stock,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await adminApi(password).delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadAll(password);
  };

  const updateOrderStatus = async (id, status) => {
    await adminApi(password).patch(`/admin/orders/${id}/status`, { status });
    toast.success("Status updated");
    loadAll(password);
  };

  if (!authed) {
    return (
      <div className="px-6 py-24 max-w-sm mx-auto" data-testid="admin-login">
        <h1 className="font-serif-display text-4xl text-center">Admin</h1>
        <p className="text-center text-sm text-[var(--srishti-text-secondary)] mt-2">
          Enter the boutique password.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            tryLogin(password);
          }}
          className="mt-8 space-y-4"
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-white border border-[var(--srishti-border)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--srishti-gold)]"
            data-testid="admin-password-input"
          />
          <button className="btn-maroon w-full" data-testid="admin-login-button">
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 md:py-16" data-testid="admin-panel">
      <div className="max-w-[1300px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif-display text-4xl">Admin</h1>
          <button
            className="text-xs uppercase tracking-widest text-[var(--srishti-text-secondary)] hover:text-[var(--srishti-maroon)]"
            onClick={() => {
              localStorage.removeItem("srishti_admin_pwd");
              setAuthed(false);
              setPassword("");
            }}
            data-testid="admin-logout"
          >
            Logout
          </button>
        </div>

        <div className="flex gap-2 border-b border-[var(--srishti-border)] mb-8 overflow-x-auto">
          {["products", "orders", "content"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap ${
                tab === t
                  ? "text-[var(--srishti-maroon)] border-b-2 border-[var(--srishti-maroon)]"
                  : "text-[var(--srishti-text-secondary)]"
              }`}
              data-testid={`admin-tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <form
              onSubmit={submitProduct}
              className="lg:col-span-1 bg-[var(--srishti-bg-alt)] border border-[var(--srishti-border)] p-6 space-y-3 sticky top-24 h-fit"
              data-testid="admin-product-form"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-serif-display text-2xl">
                  {editing ? "Edit product" : "Add product"}
                </h2>
                {editing && (
                  <button type="button" onClick={() => { setEditing(null); setForm(empty); }}>
                    <X size={18} />
                  </button>
                )}
              </div>
              <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="admin-name" />
              <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} required testid="admin-desc" textarea />
              <Input label="Price (₹)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} required testid="admin-price" />
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-semibold text-[var(--srishti-text-secondary)]">Category</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full mt-1 bg-white border border-[var(--srishti-border)] px-3 py-2 text-sm"
                  data-testid="admin-category"
                >
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </label>
              <ImageInput
                label="Main image"
                value={form.image}
                onChange={(v) => setForm({ ...form, image: v })}
                password={password}
                testid="admin-product-main-image"
              />
              <ProductExtraImages
                value={form.images_text}
                onChange={(v) => setForm({ ...form, images_text: v })}
                password={password}
              />
              <Input label="Badge (optional)" value={form.badge} onChange={(v) => setForm({ ...form, badge: v })} testid="admin-badge" />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.in_stock}
                  onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                  data-testid="admin-instock"
                />
                In stock
              </label>
              <button className="btn-maroon w-full" data-testid="admin-save-product">
                {editing ? "Save changes" : "Add product"}
              </button>
            </form>

            <div className="lg:col-span-2 space-y-3">
              {products.map((p) => (
                <div key={p.id} className="flex gap-4 border border-[var(--srishti-border)] bg-white p-3" data-testid={`admin-product-${p.id}`}>
                  <img src={p.image} alt={p.name} className="w-20 h-24 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif-display text-xl">{p.name}</p>
                    <p className="text-xs uppercase tracking-widest text-[var(--srishti-text-secondary)]">{p.category}</p>
                    <p className="text-sm text-[var(--srishti-maroon)] mt-1">₹{p.price.toLocaleString("en-IN")}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => editProduct(p)} className="p-2 border border-[var(--srishti-border)]" data-testid={`admin-edit-${p.id}`}><Pencil size={14} /></button>
                    <button onClick={() => deleteProduct(p.id)} className="p-2 border border-[var(--srishti-border)] text-[var(--srishti-maroon)]" data-testid={`admin-delete-${p.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "content" && (
          <form onSubmit={saveContent} className="max-w-3xl space-y-3" data-testid="admin-content-form">
            <p className="text-sm text-[var(--srishti-text-secondary)] mb-4">
              Edit any text or image below. Click <strong>Save</strong> to publish — changes appear instantly across the site.
            </p>

            {CONTENT_SECTIONS.map((section, idx) => {
              const isOpen = openSection === idx;
              return (
                <div
                  key={section.title}
                  className="border border-[var(--srishti-border)] bg-white"
                  data-testid={`content-section-${idx}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenSection(isOpen ? -1 : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                  >
                    <span className="font-serif-display text-lg">{section.title}</span>
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 space-y-3 border-t border-[var(--srishti-border)] pt-4 bg-[var(--srishti-bg-alt)]">
                      {section.fields.map(([key, type, label]) => (
                        <ContentField
                          key={key}
                          fieldKey={key}
                          type={type}
                          label={label}
                          value={content[key] || ""}
                          onChange={(v) => setContent({ ...content, [key]: v })}
                          password={password}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="sticky bottom-0 bg-[var(--srishti-bg)] border-t border-[var(--srishti-border)] py-4 mt-6">
              <button className="btn-maroon w-full md:w-auto" data-testid="admin-save-content">
                Save all changes
              </button>
            </div>
          </form>
        )}

        {tab === "orders" && (
          <div className="space-y-3">
            {orders.length === 0 && (
              <p className="text-sm text-[var(--srishti-text-secondary)]">No orders yet.</p>
            )}
            {orders.map((o) => (
              <div
                key={o.id}
                className="border border-[var(--srishti-border)] bg-white p-5"
                data-testid={`admin-order-${o.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-serif-display text-2xl">{o.order_number}</p>
                    <p className="text-xs uppercase tracking-widest text-[var(--srishti-text-secondary)]">
                      {o.customer_name} · {o.phone}
                    </p>
                    <p className="text-xs text-[var(--srishti-text-secondary)] mt-1">
                      Pickup: {o.pickup_date} · {o.pickup_slot}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-lg text-[var(--srishti-maroon)]">
                      ₹{o.subtotal.toLocaleString("en-IN")}
                    </p>
                    <select
                      value={o.status}
                      onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                      className="bg-[var(--srishti-bg-alt)] border border-[var(--srishti-border)] text-xs uppercase tracking-widest px-3 py-1.5"
                      data-testid={`admin-status-${o.id}`}
                    >
                      {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {o.items.map((it, idx) => (
                    <li key={idx} className="flex justify-between border-t border-[var(--srishti-border)] pt-2">
                      <span>{it.name} × {it.quantity}</span>
                      <span>₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
                    </li>
                  ))}
                </ul>
                {o.notes && (
                  <p className="mt-3 text-xs text-[var(--srishti-text-secondary)] italic">
                    Note: {o.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const ContentField = ({ fieldKey, type, label, value, onChange, password }) => {
  if (type === "url") {
    return (
      <ImageInput
        label={label}
        value={value}
        onChange={onChange}
        password={password}
        testid={`content-${fieldKey}`}
      />
    );
  }
  const cls =
    "w-full mt-1 bg-white border border-[var(--srishti-border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--srishti-gold)]";
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-[var(--srishti-text-secondary)]">
        {label}
      </span>
      {type === "textarea" ? (
        <textarea
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={`content-${fieldKey}`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          data-testid={`content-${fieldKey}`}
        />
      )}
    </label>
  );
};

const ProductExtraImages = ({ value, onChange, password }) => {
  const urls = (value || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const setAt = (i, url) => {
    const next = [...urls];
    next[i] = url;
    onChange(next.filter(Boolean).join("\n"));
  };
  const remove = (i) => {
    const next = [...urls];
    next.splice(i, 1);
    onChange(next.join("\n"));
  };
  const add = () => onChange((value || "") + (value && !value.endsWith("\n") ? "\n" : ""));
  return (
    <div className="space-y-2">
      <span className="text-xs uppercase tracking-widest font-semibold text-[var(--srishti-text-secondary)] block">
        Additional images
      </span>
      {urls.map((u, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex-1">
            <ImageInput value={u} onChange={(v) => setAt(i, v)} password={password} />
          </div>
          <button
            type="button"
            onClick={() => remove(i)}
            className="mt-1 p-2 border border-[var(--srishti-border)] text-[var(--srishti-maroon)]"
            aria-label="Remove"
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs uppercase tracking-widest font-semibold text-[var(--srishti-maroon)] flex items-center gap-1"
      >
        <Plus size={12} /> Add image
      </button>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", required, textarea, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-semibold text-[var(--srishti-text-secondary)]">{label}</span>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        rows={3}
        className="w-full mt-1 bg-white border border-[var(--srishti-border)] px-3 py-2 text-sm"
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full mt-1 bg-white border border-[var(--srishti-border)] px-3 py-2 text-sm"
        data-testid={testid}
      />
    )}
  </label>
);

